import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
  type MRT_ColumnDef,
} from 'material-react-table';

import {
  Box,
  Button,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
  lighten,
} from '@mui/material';

import { BorrowerLoan } from '../../../interface/ILoanManagement';

import helper from '../../../helpers/index';

import iconView from '../../../assets/visible.png';
import iconEdit from '../../../assets/edit-icon.png';
import iconDelete from '../../../assets/delete.png';



//example data type


const columnHelper = createMRTColumnHelper<BorrowerLoan>();

interface BorrowerLoanTableProps {
  data: BorrowerLoan[];
  redirectUrlView: string;
}


const BorrowerLoanTable: React.FC<BorrowerLoanTableProps> = ({ data, redirectUrlView }) => {

  var navigate = useNavigate();

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<BorrowerLoan>[]>(
    () => [
      {
        accessorKey: 'loanId',
        header: 'Loan ID',
        enableSorting: true
      },
      {
        // accessorKey: 'dueDate',
        // header: 'Due Date',
        // enableSorting: true
        accessorFn: (row: BorrowerLoan) => `${helper.formatLongDate(row.dueDate)}`,
        header: 'Due Date',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const firstData = new Date(rowA.original.dueDate || new Date()).getTime() || 0;
          const secondData = new Date(rowB.original.dueDate || new Date()).getTime() || 0;
          // Perform numeric comparison
          return Number(firstData) - Number(secondData);
        }
      },
      {
        // accessorKey: 'amountLoan',
        accessorFn: function(row: BorrowerLoan){
          return `${helper.formatNumber(row?.amountLoan ?? 0)}`
        },
        header: 'Amount Loan',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.amountLoan;
          const idB = rowB.original.amountLoan;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        // accessorKey: 'periodicPayments',
        accessorFn: function(row: BorrowerLoan){
          return `${ helper.formatNumber(row?.periodicPayments ?? 0)}`
        },
        header: 'Periodic Payments',
        size: 200,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.periodicPayments;
          const idB = rowB.original.periodicPayments;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        Cell: ({ renderedCellValue, row }) => {
          switch(row.original.loanStatus){
            case 0:
              return 'Ongoing';
            case 1:
              return 'Fully Paid';
            case 2:
              return 'Pending';
            default:
              return 'Declined';
          }
        },
        header: 'Status',
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.loanStatus;
          const idB = rowB.original.loanStatus;
          // Perform numeric comparison
          return Number(idA) - Number(idB);
        }
      },
      {
        header: 'Actions',
        Cell: ({ renderedCellValue, row }) => (
          <Box className="table-btn">
            <Tooltip title="View Loan">
              <Button
                onClick={() =>
                  navigate(redirectUrlView + row.original.loanId)
                }
                className="btn-icon-1"
              >
                <img className='responsive-image' src={iconView} />
              </Button>
            </Tooltip>
          </Box>
        )
      }
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data,
    muiTablePaperProps: {
      elevation: 1,
      sx: {
        boxShadow: 'none',
        width: '100%',
        background: 'transparent',
        display: 'grid',

      }
    },
    enableColumnActions: false,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.index % 2 === 0 ? 'transparent' : '#E7EFEA',
      },
    }),
  });

  return <MaterialReactTable table={table} />;
};

export default BorrowerLoanTable;
